/**
 * The word layer: an optional overlay that makes a rendered lemma's words
 * clickable (Labs' reverse index). The renderer asks for it with
 * `useWordLayer()`; the portal never provides one, so there the renderer
 * prints plain text exactly as before.
 *
 * A layer answers one question per printed text: which words are in it, and
 * where. Words carry UTF-16 ranges into the text's plain form (see
 * `wordRanges.ts`), so the renderer wraps them by position and never compares
 * spellings.
 */
import { inject, InjectionKey } from 'vue'

export type Language = 'greek' | 'hebrew' | 'latin'

/** What a language is to the index, said once (as vorlager's `Language`). */
export interface LanguageInfo {
  name: string
  /** The font class for its script ('' for the page's own). */
  font: string
  /** The language its words are aligned with, if any. */
  counterpart: Language | null
  /** How its lexeme stands to its counterpart's ("renders", "is rendered by"). */
  relation: string | null
  /** A column's words with no counterpart, as the summary counts them. */
  unaligned: string
}

export const LANGUAGES: Record<Language, LanguageInfo> = {
  greek: {
    name: 'Greek',
    font: 'font-greek',
    counterpart: 'hebrew',
    relation: 'renders',
    unaligned: 'no Hebrew (plus) or not aligned',
  },
  hebrew: {
    name: 'Hebrew',
    font: 'font-hebrew',
    counterpart: 'greek',
    relation: 'is rendered by',
    unaligned: 'omitted or not aligned',
  },
  latin: { name: 'Latin', font: '', counterpart: null, relation: null, unaligned: 'in its readings' },
}

/** What a lexeme is on a column's word: the word itself, or the text it renders (Hebrew). */
export const isRendered = (l: Language): boolean => l === 'hebrew'

export interface IndexLexeme {
  key: string
  language: Language
  headword: string
  translit: string
  gloss: string
}

/** One word of a printed text, as the reverse index knows it. */
export interface LayerWord {
  index: number
  surface: string
  word_id: string
  lexeme: IndexLexeme | null
  counterpart: IndexLexeme[]
  confident: boolean
  /** UTF-16 range in the text's plain form; null when the index couldn't place it. */
  start: number | null
  end: number | null
  /** For a Hebrew word printed whole: which of its morphemes this is. */
  morpheme: number | null
}

/** A printed text's words, valid while the text still has this fingerprint. */
export interface PrintedField {
  fingerprint: string | null
  words: LayerWord[]
}

/**
 * Which printed text of a lemma: its LXX line, its Hebrew, one of its readings
 * (by attestation), a Var reading (by variant reading) or a Wit line's scope
 * (by witness entry). As vorlager's `FieldRef`.
 */
export type FieldRef =
  | { part: 'lxx' }
  | { part: 'hebrew' }
  | { part: 'reading'; attestationId: number }
  | { part: 'var'; variantId: number }
  | { part: 'wit'; witnessEntryId: number }

/** A field as vorlager names it: its part, and the reading's id (none for the LXX or the Hebrew). */
export interface FieldName {
  part: FieldRef['part']
  id: number | null
}

/** The name of a field the page prints. */
export const fieldName = (ref: FieldRef): FieldName => {
  switch (ref.part) {
  case 'lxx':
  case 'hebrew':
    return { part: ref.part, id: null }
  case 'reading':
    return { part: ref.part, id: ref.attestationId }
  case 'var':
    return { part: ref.part, id: ref.variantId }
  case 'wit':
    return { part: ref.part, id: ref.witnessEntryId }
  }
}

/** One key per field, the same for the server's name and the page's ref. */
export const fieldKey = (name: FieldName): string => `${name.part}:${name.id ?? ''}`

/**
 * Where a piece of a printed text sits in the whole: a reading printed in
 * several pieces (its segments) is one field, fingerprinted whole.
 */
export interface Within {
  /** The whole field's plain text. */
  whole: string
  /** Where this piece starts in it. */
  offset: number
}

export interface WordLayer {
  field(ref: FieldRef): PrintedField | null
  hover(word: LayerWord | null, el: HTMLElement | null): void
  select(word: LayerWord, el: HTMLElement): void
}

export const WORD_LAYER: InjectionKey<WordLayer> = Symbol('word-layer')

export const useWordLayer = (): WordLayer | null => inject(WORD_LAYER, null)
